import {omneticApi} from '@dms/api/base';

import {
  GetVehicleApiResponse,
  GetVehicleApiArg,
  UpdateVehicleApiResponse,
  UpdateVehicleApiArg,
  CreateVehicleApiResponse,
  CreateVehicleApiArg,
  RegisterVehicleForSaleV2ApiResponse,
  RegisterVehicleForSaleV2ApiArg,
  RegisterVehicleToBuyApiResponse,
  RegisterVehicleToBuyApiArg,
  PatchSaleVehicleSourceCodeApiResponse,
  PatchSaleVehicleSourceCodeApiArg,
  CloneSaleVehicleApiResponse,
  CloneSaleVehicleApiArg,
} from './types';

export const vehicleOperationsApi = omneticApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (build) => ({
    /***** Base Vehicle *****/
    getVehicleOperation: build.query<GetVehicleApiResponse, GetVehicleApiArg>({
      query: (queryArg) => ({
        url: `/dms/v2/vehicle/${queryArg.vehicleId}`,
      }),
      providesTags: (_result, _error, queryArg) => [{type: 'Vehicle', id: queryArg.vehicleId}],
    }),
    updateVehicleOperation: build.mutation<UpdateVehicleApiResponse, UpdateVehicleApiArg>({
      query: (queryArg) => ({
        url: `/dms/v2/vehicle/${queryArg.vehicleId}`,
        method: 'PUT',
        body: queryArg.updateVehicleRequestBody,
        params: {validateMandatoryFields: queryArg.validateMandatoryFields},
      }),
      invalidatesTags: (_result, _error, queryArg) => [
        {type: 'Vehicle', id: queryArg.vehicleId},
        {type: 'SaleVehicle', id: `sale_vehicle_from_vehicle_id_${queryArg.vehicleId}`},
        {type: 'SimilarVehiclesCount', id: queryArg.vehicleId},
        'AdGeneralSettings',
        'AdPlatformSettings',
        'AdWindshieldSettings',
        'CustomTenantCatalogue',
      ],
    }),
    createVehicleOperation: build.mutation<CreateVehicleApiResponse, CreateVehicleApiArg>({
      query: (queryArg) => ({
        url: `/dms/v2/vehicle`,
        method: 'POST',
        body: queryArg.createVehicleRequestBody,
        params: {validateMandatoryFields: queryArg.validateMandatoryFields},
      }),
      invalidatesTags: [
        'AdGeneralSettings',
        'AdPlatformSettings',
        'AdWindshieldSettings',
        'SimilarVehiclesCount',
        'CustomTenantCatalogue',
      ],
    }),
    registerVehicleForSale: build.mutation<
      RegisterVehicleForSaleV2ApiResponse,
      RegisterVehicleForSaleV2ApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/vehicle/${queryArg.vehicleId}/register-for-sale`,
        method: 'PUT',
        body: queryArg.registerVehicleForSaleV2RequestBody,
      }),
      invalidatesTags: (_result, _error, queryArg) => [
        {type: 'Vehicle', id: queryArg.vehicleId},
        {type: 'SaleVehicle', id: queryArg.vehicleId},
        {type: 'SaleVehicle', id: `sale_vehicle_from_vehicle_id_${queryArg.vehicleId}`},
        {type: 'SaleVehicleActions', id: queryArg.vehicleId},
        {type: 'VehicleLastActiveBC', id: queryArg.vehicleId},
      ],
    }),
    registerVehicleToBuy: build.mutation<
      RegisterVehicleToBuyApiResponse,
      RegisterVehicleToBuyApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/vehicle/${queryArg.vehicleId}/register-to-buy`,
        method: 'PUT',
        body: queryArg.registerVehicleToBuyRequestBody,
      }),
      invalidatesTags: (_result, _error, queryArg) => [{type: 'Vehicle', id: queryArg.vehicleId}],
    }),
    patchSaleVehicleSourceCode: build.mutation<
      PatchSaleVehicleSourceCodeApiResponse,
      PatchSaleVehicleSourceCodeApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sales/sale-vehicle/${queryArg.vehicleId}/source-code`,
        method: 'PATCH',
        body: queryArg.patchSaleVehicleSourceCodeRequestBody,
      }),
    }),
    cloneSaleVehicle: build.mutation<CloneSaleVehicleApiResponse, CloneSaleVehicleApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/vehicle/${queryArg.vehicleId}/clone-sales`,
        method: 'POST',
        body: queryArg.cloneSaleVehicleRequestBody,
      }),
      invalidatesTags: (_result, _error, queryArg) => [
        {type: 'Vehicle', id: queryArg.cloneSaleVehicleRequestBody.toVehicleId},
      ],
    }),
  }),
});

const {
  useCreateVehicleOperationMutation: useCreateVehicleMutation,
  useGetVehicleOperationQuery: useGetVehicleQuery,
  useLazyGetVehicleOperationQuery: useLazyGetVehicleQuery,
  useUpdateVehicleOperationMutation: useUpdateVehicleMutation,
  useRegisterVehicleForSaleMutation,
  useRegisterVehicleToBuyMutation,
  usePatchSaleVehicleSourceCodeMutation,
  useCloneSaleVehicleMutation,
} = vehicleOperationsApi;

export {
  /***** Base Vehicle *****/
  useCreateVehicleMutation,
  useGetVehicleQuery,
  useLazyGetVehicleQuery,
  useUpdateVehicleMutation,
  /***** Sale Vehicle *****/
  useRegisterVehicleForSaleMutation,
  useRegisterVehicleToBuyMutation,
  usePatchSaleVehicleSourceCodeMutation,
  useCloneSaleVehicleMutation,
};
